// Analytics Service - The Big Brother
import { EventType } from "@prisma/client";
import {
  logAnalyticsError,
  logAnalyticsWarning,
  logAnalyticsDebug,
  isBrowserExtensionError
} from "@/lib/analytics/analytics-logger";

// Client-side session management
let sessionId: string | null = null;
let sessionInitialized = false;

// Prevent recursive error tracking
let isTrackingError = false;
let lastErrorTime = 0;
const ERROR_THROTTLE_MS = 1000; // Throttle errors to once per second

// Extension detection - disable analytics when extension interference is detected
let analyticsDisabled = false;
let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 3;

// Visitor tracking constants
const VISITOR_COOKIE_NAME = 'usland_visitor_id';
const VISITOR_COOKIE_EXPIRY_DAYS = 730; // 2 years

/**
 * Safe fetch wrapper for analytics API calls
 * Silently ignores errors caused by browser extensions
 */
async function analyticsFetch(url: string, options?: RequestInit): Promise<Response | null> {
  // Skip if analytics is disabled due to extension interference
  if (analyticsDisabled) {
    return null;
  }

  try {
    const response = await fetch(url, options);

    // Check for response errors to catch extension interference
    if (response && !response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    // Reset failure counter on success
    consecutiveFailures = 0;
    return response;
  } catch (error) {
    if (isBrowserExtensionError(error)) {
      consecutiveFailures++;
      // Disable analytics after multiple consecutive extension failures
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        analyticsDisabled = true;
        logAnalyticsWarning("Analytics disabled due to browser extension interference");
      }
    } else {
      logAnalyticsError("Analytics fetch failed", error, { url });
    }

    return null;
  }
}

// Helper function to generate UUID - works in both browser and Node.js
function generateUUID(): string {
  if (typeof window !== "undefined" && typeof window.crypto !== "undefined" && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for environments where crypto.randomUUID is not available
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";

  if (!sessionId) {
    sessionId = localStorage.getItem("usland_session_id");
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("usland_session_id", sessionId);
    }
  }

  return sessionId;
}

// Visitor ID management with persistent cookies
export function getOrCreateVisitorId(): string {
  if (typeof window === "undefined") return "";

  let visitorId = getCookie(VISITOR_COOKIE_NAME);

  if (!visitorId) {
    visitorId = `visitor_${generateUUID()}`;
    setCookie(VISITOR_COOKIE_NAME, visitorId, VISITOR_COOKIE_EXPIRY_DAYS);
  }

  return visitorId;
}

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

// Initialize session with backend
export async function initializeAnalyticsSession(): Promise<void> {
  if (typeof window === "undefined" || sessionInitialized) return;

  try {
    const sessionId = getOrCreateSessionId();
    const { browser, os } = getBrowserInfo();
    const deviceType = getDeviceType();
    const visitorId = getOrCreateVisitorId();

    // Parse UTM parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get("utm_source") || undefined;
    const utmMedium = urlParams.get("utm_medium") || undefined;
    const utmCampaign = urlParams.get("utm_campaign") || undefined;
    const utmContent = urlParams.get("utm_content") || undefined;
    const utmTerm = urlParams.get("utm_term") || undefined;

    await analyticsFetch("/api/analytics/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        visitorId,
        action: "start",
        entryUrl: window.location.href,
        entryPage: window.location.pathname,
        deviceType,
        browser,
        os,
        userAgent: navigator.userAgent,
        referrer: document.referrer || undefined,
        utmSource,
        utmMedium,
        utmCampaign,
        utmContent,
        utmTerm
      })
    });

    sessionInitialized = true;
  } catch (error) {
    logAnalyticsError("Failed to initialize analytics session", error);
  }
}

// Update session stats
export async function updateSessionStats(updates: {
  pageViews?: number;
  interactions?: number;
  scrollDepth?: number;
  signedUp?: boolean;
  signedIn?: boolean;
  createdListing?: boolean;
  viewedListings?: number;
  contactedSeller?: boolean;
}): Promise<void> {
  try {
    await analyticsFetch("/api/analytics/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId: getOrCreateSessionId(),
        action: "update",
        ...updates
      })
    });
  } catch (error) {
    logAnalyticsError("Failed to update session stats", error);
  }
}

export interface TrackEventParams {
  eventType: EventType;
  eventCategory: string;
  eventAction: string;
  eventLabel?: string;
  eventValue?: number;
  listingId?: string;
  listingPrice?: number;
  listingType?: string;
  elementId?: string;
  elementClass?: string;
  elementText?: string;
  metadata?: Record<string, any>;
}

export interface PageViewParams {
  pagePath: string;
  pageTitle?: string;
  referrer?: string;
  loadTime?: number;
}

// Device detection utilities
export function getDeviceType(): string {
  if (typeof window === "undefined") return "unknown";

  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "mobile";
  }
  return "desktop";
}

export function getBrowserInfo(): { browser: string; os: string } {
  if (typeof window === "undefined") return { browser: "unknown", os: "unknown" };

  const ua = navigator.userAgent;
  let browser = "Unknown";
  let os = "Unknown";

  // Browser detection
  if (ua.indexOf("Firefox") > -1) browser = "Firefox";
  else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) browser = "Opera";
  else if (ua.indexOf("Trident") > -1) browser = "IE";
  else if (ua.indexOf("Edge") > -1) browser = "Edge";
  else if (ua.indexOf("Chrome") > -1) browser = "Chrome";
  else if (ua.indexOf("Safari") > -1) browser = "Safari";

  // OS detection
  if (ua.indexOf("Win") > -1) os = "Windows";
  else if (ua.indexOf("Mac") > -1) os = "MacOS";
  else if (ua.indexOf("X11") > -1) os = "UNIX";
  else if (ua.indexOf("Linux") > -1) os = "Linux";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";

  return { browser, os };
}

export function getScreenDimensions(): { width: number; height: number } {
  if (typeof window === "undefined") return { width: 0, height: 0 };
  return {
    width: window.screen.width,
    height: window.screen.height
  };
}

// Track any user event
export async function trackEvent(params: TrackEventParams): Promise<void> {
  // Don't track errors while tracking errors to prevent recursion
  if (isTrackingError && params.eventCategory === "error") {
    return;
  }

  try {
    const sessionId = getOrCreateSessionId();
    const { browser, os } = getBrowserInfo();
    const { width, height } = getScreenDimensions();

    const eventData = {
      sessionId,
      eventType: params.eventType,
      eventCategory: params.eventCategory,
      eventAction: params.eventAction,
      eventLabel: params.eventLabel,
      eventValue: params.eventValue,
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      pageTitle: document.title,
      referrer: document.referrer || undefined,
      elementId: params.elementId,
      elementClass: params.elementClass,
      elementText: params.elementText,
      listingId: params.listingId,
      listingPrice: params.listingPrice,
      listingType: params.listingType,
      userAgent: navigator.userAgent,
      deviceType: getDeviceType(),
      browser,
      os,
      screenWidth: width,
      screenHeight: height,
      metadata: params.metadata,
      timestamp: new Date().toISOString()
    };

    // Send to analytics endpoint with error handling
    await analyticsFetch("/api/analytics/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventData),
      // Use keepalive for events that happen during page unload
      keepalive: true
    });
  } catch (error) {
    // Silently fail - error will be logged via analytics logger
    logAnalyticsError("Failed to track event", error, { eventType: params.eventType });
  }
}

// Track page views
export async function trackPageView(params: PageViewParams): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId();

    const pageViewData = {
      sessionId,
      pageUrl: window.location.href,
      pagePath: params.pagePath,
      pageTitle: params.pageTitle || document.title,
      referrer: params.referrer || document.referrer || undefined,
      loadTime: params.loadTime
    };

    // Send to analytics endpoint with error handling
    try {
      await analyticsFetch("/api/analytics/pageview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pageViewData)
      });
    } catch (error) {
      logAnalyticsError("Failed to send page view to API", error, { pagePath: params.pagePath });
    }

    // Also track as an event
    try {
      await trackEvent({
        eventType: "PAGE_VIEW" as EventType,
        eventCategory: "navigation",
        eventAction: "page_view",
        eventLabel: params.pagePath
      });
    } catch (error) {
      logAnalyticsError("Failed to track page view as event", error);
    }
  } catch (error) {
    logAnalyticsError("Failed to track page view", error);
  }
}

// Track clicks with element details
export async function trackClick(
  element: HTMLElement,
  category: string = "interaction"
): Promise<void> {
  const elementId = element.id || undefined;
  const elementClass = getElementClassName(element);
  const elementText = element.textContent?.trim().substring(0, 100) || undefined;
  const elementTag = element.tagName.toLowerCase();

  await trackEvent({
    eventType: "CLICK" as EventType,
    eventCategory: category,
    eventAction: "click",
    eventLabel: elementText,
    elementId,
    elementClass,
    metadata: {
      elementTag,
      href: element.getAttribute("href") || undefined
    }
  });
}

// Track scroll depth
let maxScrollDepth = 0;
export function trackScrollDepth(): void {
  if (typeof window === "undefined") return;

  const calculateScrollDepth = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    const scrollDepth = ((scrollTop + windowHeight) / documentHeight) * 100;

    if (scrollDepth > maxScrollDepth) {
      maxScrollDepth = scrollDepth;

      // Track at 25%, 50%, 75%, 100%
      if (
        maxScrollDepth >= 25 &&
        maxScrollDepth < 50 &&
        !sessionStorage.getItem("scroll_25")
      ) {
        sessionStorage.setItem("scroll_25", "true");
        trackEvent({
          eventType: "SCROLL" as EventType,
          eventCategory: "engagement",
          eventAction: "scroll",
          eventLabel: "25%",
          eventValue: 25
        });
      } else if (
        maxScrollDepth >= 50 &&
        maxScrollDepth < 75 &&
        !sessionStorage.getItem("scroll_50")
      ) {
        sessionStorage.setItem("scroll_50", "true");
        trackEvent({
          eventType: "SCROLL" as EventType,
          eventCategory: "engagement",
          eventAction: "scroll",
          eventLabel: "50%",
          eventValue: 50
        });
      } else if (
        maxScrollDepth >= 75 &&
        maxScrollDepth < 100 &&
        !sessionStorage.getItem("scroll_75")
      ) {
        sessionStorage.setItem("scroll_75", "true");
        trackEvent({
          eventType: "SCROLL" as EventType,
          eventCategory: "engagement",
          eventAction: "scroll",
          eventLabel: "75%",
          eventValue: 75
        });
      } else if (maxScrollDepth >= 100 && !sessionStorage.getItem("scroll_100")) {
        sessionStorage.setItem("scroll_100", "true");
        trackEvent({
          eventType: "SCROLL" as EventType,
          eventCategory: "engagement",
          eventAction: "scroll",
          eventLabel: "100%",
          eventValue: 100
        });
      }
    }
  };

  window.addEventListener("scroll", calculateScrollDepth, { passive: true });
}

// Track form interactions
export async function trackFormFocus(formName: string, fieldName: string): Promise<void> {
  await trackEvent({
    eventType: "FORM_FOCUS" as EventType,
    eventCategory: "form",
    eventAction: "focus",
    eventLabel: `${formName}.${fieldName}`
  });
}

export async function trackFormSubmit(
  formName: string,
  success: boolean,
  metadata?: Record<string, any>
): Promise<void> {
  await trackEvent({
    eventType: "FORM_SUBMIT" as EventType,
    eventCategory: "form",
    eventAction: success ? "submit_success" : "submit_error",
    eventLabel: formName,
    metadata
  });
}

// Track listing interactions
export async function trackListingView(
  listingId: string,
  listingPrice?: number,
  listingType?: string
): Promise<void> {
  const sessionId = getOrCreateSessionId();

  // Track as an event
  try {
    await trackEvent({
      eventType: "LISTING_VIEW" as EventType,
      eventCategory: "listing",
      eventAction: "view",
      listingId,
      listingPrice,
      listingType
    });
  } catch (error) {
    logAnalyticsError("Failed to track listing view event", error, { listingId });
  }

  // Also create a ListingView record for history
  try {
    await analyticsFetch("/api/analytics/listing-view", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        listingId,
        sessionId,
        referrer: document.referrer || undefined,
        source: "search"
      }),
      // Use keepalive for views that happen during navigation
      keepalive: true
    });
  } catch (error) {
    logAnalyticsError("Failed to create listing view record", error, { listingId });
  }
}

export async function trackListingContact(listingId: string): Promise<void> {
  await trackEvent({
    eventType: "LISTING_CONTACT" as EventType,
    eventCategory: "listing",
    eventAction: "contact",
    listingId
  });
}

// Track search behavior
export async function trackSearch(
  searchTerm: string,
  resultsCount: number,
  filters?: Record<string, any>
): Promise<void> {
  await trackEvent({
    eventType: "SEARCH_PERFORM" as EventType,
    eventCategory: "search",
    eventAction: "search",
    eventLabel: searchTerm,
    eventValue: resultsCount,
    metadata: filters
  });
}

// Track heatmap clicks
export async function trackHeatmapClick(x: number, y: number, element: HTMLElement): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId();

    const heatmapData = {
      sessionId,
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      clickX: x,
      clickY: y,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      elementTag: element.tagName.toLowerCase(),
      elementId: element.id || undefined,
      elementClass: getElementClassName(element)
    };

    await analyticsFetch("/api/analytics/heatmap", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(heatmapData)
    });
  } catch (error) {
    logAnalyticsError("Failed to track heatmap click", error);
  }
}

// Helper function to safely get element class name as string
function getElementClassName(element: HTMLElement): string | undefined {
  try {
    // Handle SVG elements and other elements with classList
    if (element.className) {
      // For SVG elements, className is an object (SVGAnimatedString)
      if (typeof element.className === 'object' && 'baseVal' in element.className) {
        const baseVal = (element.className as any).baseVal;
        return baseVal && baseVal.length > 0 ? baseVal : undefined;
      }
      // For regular elements, className is a string
      if (typeof element.className === 'string') {
        return element.className.length > 0 ? element.className : undefined;
      }
    }
    // Fallback to classList
    if (element.classList && element.classList.length > 0) {
      return Array.from(element.classList).join(' ');
    }
    return undefined;
  } catch {
    return undefined;
  }
}

// RAGE CLICK DETECTION
let clickHistory: Array<{ element: HTMLElement; timestamp: number }> = [];

export function detectRageClick(element: HTMLElement): void {
  const now = Date.now();
  clickHistory.push({ element, timestamp: now });

  // Keep only clicks from last 2 seconds
  clickHistory = clickHistory.filter((click) => now - click.timestamp < 2000);

  // Check for rage clicks (3+ clicks on same element within 2 seconds)
  const sameElementClicks = clickHistory.filter(
    (click) => click.element === element
  );

  if (sameElementClicks.length >= 3) {
    const firstClick = sameElementClicks[0];
    if (!firstClick) return;

    // RAGE CLICK DETECTED!
    trackEvent({
      eventType: "RAGE_CLICK" as EventType,
      eventCategory: "frustration",
      eventAction: "rage_click",
      eventLabel: element.tagName,
      elementId: element.id || undefined,
      elementClass: getElementClassName(element),
      elementText: element.textContent?.trim().substring(0, 100),
      metadata: {
        clickCount: sameElementClicks.length,
        timeWindow: now - firstClick.timestamp
      }
    });

    // Also send to frustration endpoint
    analyticsFetch("/api/analytics/frustration", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId: getOrCreateSessionId(),
        signalType: "RAGE_CLICK",
        pageUrl: window.location.href,
        pagePath: window.location.pathname,
        elementId: element.id || undefined,
        elementClass: getElementClassName(element),
        clickCount: sameElementClicks.length,
        timeWindow: now - firstClick.timestamp
      })
    });

    // Clear history to avoid duplicate reports
    clickHistory = [];
  }
}

// DEAD CLICK DETECTION (clicks on non-interactive elements)
export function detectDeadClick(element: HTMLElement): void {
  const interactiveTags = ["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA"];
  const isInteractive =
    interactiveTags.includes(element.tagName) ||
    element.hasAttribute("onclick") ||
    element.style.cursor === "pointer" ||
    element.getAttribute("role") === "button";

  if (!isInteractive) {
    trackEvent({
      eventType: "DEAD_CLICK" as EventType,
      eventCategory: "frustration",
      eventAction: "dead_click",
      eventLabel: element.tagName,
      elementId: element.id || undefined,
      elementClass: getElementClassName(element),
      elementText: element.textContent?.trim().substring(0, 100)
    });
  }
}

// MOUSE MOVEMENT TRACKING (sampled to avoid overload)
let mouseMovements: Array<{ x: number; y: number; timestamp: number }> = [];
let mouseMoveStartTime: number | null = null;

export function trackMouseMovement(): void {
  if (typeof window === "undefined") return;

  let lastSample = 0;
  const SAMPLE_RATE = 200; // Sample every 200ms
  const BATCH_DURATION = 10000; // Send batch every 10 seconds

  const handleMouseMove = (e: MouseEvent) => {
    const now = Date.now();

    // Initialize start time
    if (!mouseMoveStartTime) {
      mouseMoveStartTime = now;
    }

    // Sample mouse position
    if (now - lastSample >= SAMPLE_RATE) {
      mouseMovements.push({
        x: e.clientX,
        y: e.clientY,
        timestamp: now
      });
      lastSample = now;
    }

    // Send batch if we've collected enough data
    if (now - mouseMoveStartTime >= BATCH_DURATION && mouseMovements.length > 0) {
      sendMouseMovementBatch();
    }
  };

  window.addEventListener("mousemove", handleMouseMove, { passive: true });
}

async function sendMouseMovementBatch(): Promise<void> {
  if (mouseMovements.length === 0) return;

  try {
    await analyticsFetch("/api/analytics/mouse-movement", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId: getOrCreateSessionId(),
        pageUrl: window.location.href,
        pagePath: window.location.pathname,
        movements: mouseMovements,
        startTime: new Date(mouseMoveStartTime!).toISOString(),
        endTime: new Date(Date.now()).toISOString()
      })
    });
  } catch (error) {
    logAnalyticsError("Failed to send mouse movement batch", error);
  }

  // Reset
  mouseMovements = [];
  mouseMoveStartTime = null;
}

// TAB VISIBILITY TRACKING
export function trackTabVisibility(): void {
  if (typeof document === "undefined") return;

  let tabHiddenTime: number | null = null;

  const handleVisibilityChange = () => {
    if (document.hidden) {
      tabHiddenTime = Date.now();
      trackEvent({
        eventType: "TAB_HIDDEN" as EventType,
        eventCategory: "engagement",
        eventAction: "tab_hidden",
        eventLabel: window.location.pathname
      });
    } else {
      const timeHidden = tabHiddenTime ? Date.now() - tabHiddenTime : 0;
      trackEvent({
        eventType: "TAB_VISIBLE" as EventType,
        eventCategory: "engagement",
        eventAction: "tab_visible",
        eventLabel: window.location.pathname,
        eventValue: timeHidden
      });
      tabHiddenTime = null;
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
}

// COPY/PASTE TRACKING
export function trackCopyPaste(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("copy", (e) => {
    const selection = window.getSelection()?.toString();
    trackEvent({
      eventType: "COPY" as EventType,
      eventCategory: "interaction",
      eventAction: "copy",
      eventLabel: selection?.substring(0, 100)
    });
  });

  window.addEventListener("paste", () => {
    trackEvent({
      eventType: "PASTE" as EventType,
      eventCategory: "interaction",
      eventAction: "paste",
      eventLabel: window.location.pathname
    });
  });
}

// TEXT SELECTION TRACKING
export function trackTextSelection(): void {
  if (typeof window === "undefined") return;

  let selectionTimeout: NodeJS.Timeout;

  window.addEventListener("mouseup", () => {
    clearTimeout(selectionTimeout);
    selectionTimeout = setTimeout(() => {
      const selection = window.getSelection();
      const selectedText = selection?.toString().trim();

      if (selectedText && selectedText.length > 5) {
        trackEvent({
          eventType: "SELECTION" as EventType,
          eventCategory: "engagement",
          eventAction: "text_selection",
          eventLabel: selectedText.substring(0, 100),
          eventValue: selectedText.length
        });
      }
    }, 300);
  });
}

// KEYBOARD SHORTCUTS TRACKING
export function trackKeyboardShortcuts(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("keydown", (e) => {
    // Track common shortcuts
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
      const key = e.key.toLowerCase();
      const trackableKeys = ["s", "p", "f", "k", "n", "t", "w"];

      if (trackableKeys.includes(key)) {
        trackEvent({
          eventType: "KEYBOARD" as EventType,
          eventCategory: "shortcut",
          eventAction: "keyboard_shortcut",
          eventLabel: `${e.ctrlKey ? "Ctrl" : "Cmd"}+${key.toUpperCase()}`
        });
      }
    }
  });
}

// CONTEXT MENU TRACKING (right-click)
export function trackContextMenu(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("contextmenu", (e) => {
    const target = e.target as HTMLElement;
    trackEvent({
      eventType: "CONTEXT_MENU" as EventType,
      eventCategory: "interaction",
      eventAction: "right_click",
      elementId: target.id || undefined,
      elementClass: getElementClassName(target),
      elementText: target.textContent?.trim().substring(0, 100)
    });
  });
}

// NETWORK PERFORMANCE TRACKING
export function trackNetworkPerformance(): void {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) return;

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.entryType === "navigation") {
        const navEntry = entry as PerformanceNavigationTiming;

        trackEvent({
          eventType: "PAGE_VIEW" as EventType,
          eventCategory: "performance",
          eventAction: "page_load",
          eventLabel: window.location.pathname,
          metadata: {
            dns: navEntry.domainLookupEnd - navEntry.domainLookupStart,
            tcp: navEntry.connectEnd - navEntry.connectStart,
            ttfb: navEntry.responseStart - navEntry.requestStart,
            download: navEntry.responseEnd - navEntry.responseStart,
            domInteractive: navEntry.domInteractive - navEntry.fetchStart,
            domComplete: navEntry.domComplete - navEntry.fetchStart,
            loadComplete: navEntry.loadEventEnd - navEntry.fetchStart
          }
        });

        // Flag slow networks
        if (navEntry.loadEventEnd - navEntry.fetchStart > 5000) {
          trackEvent({
            eventType: "NETWORK_SLOW" as EventType,
            eventCategory: "performance",
            eventAction: "slow_network",
            eventValue: navEntry.loadEventEnd - navEntry.fetchStart
          });
        }
      }
    }
  });

  observer.observe({ entryTypes: ["navigation"] });
}

// ERROR TRACKING
export function trackErrors(): void {
  if (typeof window === "undefined") return;

  // Track JavaScript errors
  window.addEventListener("error", (e) => {
    // Prevent recursive error tracking and throttle
    if (isTrackingError) return;
    const now = Date.now();
    if (now - lastErrorTime < ERROR_THROTTLE_MS) return;

    isTrackingError = true;
    lastErrorTime = now;

    // Don't await - fire and forget to avoid blocking
    trackEvent({
      eventType: "ERROR" as EventType,
      eventCategory: "error",
      eventAction: "javascript_error",
      eventLabel: e.message?.substring(0, 200),
      metadata: {
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error?.stack ? String(e.error.stack).substring(0, 500) : undefined
      }
    }).finally(() => {
      isTrackingError = false;
    });
  });

  // Track unhandled promise rejections
  window.addEventListener("unhandledrejection", (e) => {
    // Prevent recursive error tracking and throttle
    if (isTrackingError) return;
    const now = Date.now();
    if (now - lastErrorTime < ERROR_THROTTLE_MS) return;

    isTrackingError = true;
    lastErrorTime = now;

    // Filter out extension-related errors
    const reasonStr = String(e.reason);
    if (reasonStr.includes('chrome-extension://') ||
        reasonStr.includes('Failed to fetch')) {
      return;
    }

    // Don't await - fire and forget to avoid blocking
    trackEvent({
      eventType: "ERROR" as EventType,
      eventCategory: "error",
      eventAction: "unhandled_rejection",
      eventLabel: reasonStr.substring(0, 200),
      metadata: {
        reason: reasonStr.substring(0, 500)
      }
    }).finally(() => {
      isTrackingError = false;
    });
  });
}

// Initialize all tracking
export function initializeAdvancedTracking(): void {
  if (typeof window === "undefined") return;

  trackMouseMovement();
  trackTabVisibility();
  trackCopyPaste();
  trackTextSelection();
  trackKeyboardShortcuts();
  trackContextMenu();
  trackNetworkPerformance();
  trackErrors();
}
